import { API_CONFIG, PLATFORM_ENDPOINTS, getHeadersWithOrg, apiCall, authFetch } from './client';
import i18n from '../../i18n';
import { buildPaginatedQueryParams, type SortOptions } from './paginationParams';
import type { ColumnFilter } from '../../components/common/FilterableTable/types';
import {
  ErrorResponse,
  FeedbackSummaryResponse,
  AllSitesFeedbackSummaryResponse,
  TopicQualityListResponse,
  UserFeedbackSummaryResponse,
  UserFeedbackTrendsResponse,
  FeedbackComparisonResponse,
  FeedbackRowsResponse,
  UsageTrendsResponse,
  TopUsersResponse,
  ReferencedTopicsResponse,
  LLMLatencyTrendsResponse,
  AnswerSuccessRateResponse,
  CacheHitRateResponse,
  RatingTrendsResponse,
  UnansweredQuestionsResponse,
} from '../../types';

// =============================================================================
// Feedback Analytics API Functions
// =============================================================================

/**
 * Fetch the feedback summary for a single site.
 * Returns average quality scores and total evaluation count.
 * Returns null if no feedback data exists (404).
 */
export async function fetchSiteFeedbackSummary(
  siteId: string,
  orgId: string,
): Promise<FeedbackSummaryResponse | null> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.FEEDBACK_SUMMARY(siteId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch feedback summary: ${response.status}`);
    }

    const data: FeedbackSummaryResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.analytics.feedbackSummaryFetchFailed'));
  }
}

/**
 * Fetch feedback summaries across all sites.
 */
export async function fetchAllSitesFeedbackSummary(
  orgId: string,
): Promise<AllSitesFeedbackSummaryResponse> {
  return apiCall<AllSitesFeedbackSummaryResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.ALL_SITES_FEEDBACK_SUMMARY(orgId)}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch the topics with the lowest average quality scores for a site.
 * @param source - 'judge' (default) for LLM-as-Judge scores or 'user' for user feedback scores
 */
export async function fetchBottomTopics(
  siteId: string,
  orgId: string,
  minEvals: number = 1,
  limit: number = 10,
  source: 'judge' | 'user' = 'judge',
): Promise<TopicQualityListResponse> {
  return apiCall<TopicQualityListResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.BOTTOM_TOPICS(siteId)}?min_evals=${minEvals}&limit=${limit}&source=${source}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch the topics with the highest average quality scores for a site.
 * @param source - 'judge' (default) for LLM-as-Judge scores or 'user' for user feedback scores
 */
export async function fetchTopTopics(
  siteId: string,
  orgId: string,
  minEvals: number = 1,
  limit: number = 10,
  source: 'judge' | 'user' = 'judge',
): Promise<TopicQualityListResponse> {
  return apiCall<TopicQualityListResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.TOP_TOPICS(siteId)}?min_evals=${minEvals}&limit=${limit}&source=${source}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch a paginated list of rated conversation turns (thumbs-up or thumbs-down).
 * Server-side enforces the rated-only filter; UI does not need to send it.
 */
export async function fetchFeedbackRows(
  siteId: string,
  _orgId: string,
  page: number = 1,
  pageSize: number = 20,
  filters?: ColumnFilter[],
  sort?: SortOptions,
): Promise<FeedbackRowsResponse> {
  const params = buildPaginatedQueryParams(page, pageSize, filters, sort);
  return apiCall<FeedbackRowsResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.FEEDBACK_ROWS(siteId)}?${params.toString()}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

// =============================================================================
// User Feedback Analytics API Functions
// =============================================================================

/**
 * Fetch aggregated user feedback summary for a site.
 * Returns average quality scores and unique user count.
 * Returns null if no user feedback data exists (404).
 */
export async function fetchUserFeedbackSummary(
  siteId: string,
  orgId: string,
): Promise<UserFeedbackSummaryResponse | null> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USER_FEEDBACK_SUMMARY(siteId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch user feedback summary: ${response.status}`);
    }

    const data: UserFeedbackSummaryResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.analytics.userFeedbackSummaryFetchFailed'));
  }
}

/**
 * Fetch time-series quality trends from user feedback.
 * Returns scores bucketed by day, week, or month.
 */
export async function fetchUserFeedbackTrends(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'week',
): Promise<UserFeedbackTrendsResponse> {
  return apiCall<UserFeedbackTrendsResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USER_FEEDBACK_TRENDS(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch comparison between user feedback and LLM-as-Judge scores.
 * Returns per-dimension MAE and verdict agreement rate.
 * Returns null if no matched turns found (404).
 */
export async function fetchFeedbackComparison(
  siteId: string,
  orgId: string,
): Promise<FeedbackComparisonResponse | null> {
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.FEEDBACK_COMPARISON(siteId)}`;

  try {
    const response = await authFetch(url, {
      method: 'GET',
      headers: getHeadersWithOrg(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData: ErrorResponse = await response.json();
      throw new Error(errorData.detail || errorData.error || `Failed to fetch feedback comparison: ${response.status}`);
    }

    const data: FeedbackComparisonResponse = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(i18n.t('errors.analytics.feedbackComparisonFetchFailed'));
  }
}

// =============================================================================
// Usage Analytics API Functions
// =============================================================================

/**
 * Fetch usage trends (queries over time) for a site.
 * Pass `channel` to filter by a specific channel (app, x-api-key-mcp, x-api-key-widget).
 */
export async function fetchUsageTrends(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'day',
  channel?: string,
): Promise<UsageTrendsResponse> {
  let url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_TRENDS(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`;
  if (channel) {
    url += `&channel=${encodeURIComponent(channel)}`;
  }

  return apiCall<UsageTrendsResponse>(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });
}

/**
 * Fetch top users by query count for a site.
 * @param includeChannelBreakdown When true, returns per-user breakdown by channel (app, mcp, etc.).
 * @param startDate Optional start date (ISO YYYY-MM-DD) for filtering queries.
 * @param endDate Optional end date (ISO YYYY-MM-DD) for filtering queries.
 */
export async function fetchTopUsers(
  siteId: string,
  orgId: string,
  limit: number = 10,
  includeChannelBreakdown: boolean = false,
  startDate?: string,
  endDate?: string,
): Promise<TopUsersResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (includeChannelBreakdown) params.set('include_channel_breakdown', 'true');
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);

  return apiCall<TopUsersResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_TOP_USERS(siteId)}?${params.toString()}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch LLM latency trends (average latency per time bucket, grouped by model).
 * Channel-agnostic; aggregates all channels.
 */
export async function fetchLLMLatencyTrends(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'day',
): Promise<LLMLatencyTrendsResponse> {
  return apiCall<LLMLatencyTrendsResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_LLM_LATENCY(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch answer success rate (answered vs. unanswered queries over time).
 */
export async function fetchAnswerSuccessRate(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'day',
): Promise<AnswerSuccessRateResponse> {
  return apiCall<AnswerSuccessRateResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_ANSWER_SUCCESS(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch a paginated, sortable list of unanswered questions for a site.
 * These are user questions for which the system could not produce an answer,
 * useful for identifying content gaps.
 */
export async function fetchUnansweredQuestions(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  page: number = 1,
  pageSize: number = 20,
  sortOrder: 'asc' | 'desc' = 'desc',
): Promise<UnansweredQuestionsResponse> {
  const params = new URLSearchParams({
    start_date: startDate,
    end_date: endDate,
    page: String(page),
    page_size: String(pageSize),
    sort_order: sortOrder,
  });
  const url = `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.UNANSWERED_QUESTIONS(siteId)}?${params}`;

  const response = await authFetch(url, {
    method: 'GET',
    headers: getHeadersWithOrg(),
  });

  if (!response.ok) {
    const errorData: ErrorResponse = await response.json();
    throw new Error(errorData.detail || errorData.error || `Failed to fetch unanswered questions: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch rating trends (thumbs up/down over time) for a site.
 */
export async function fetchRatingTrends(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'day',
): Promise<RatingTrendsResponse> {
  return apiCall<RatingTrendsResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_RATING_TRENDS(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

export async function fetchCacheHitRate(
  siteId: string,
  orgId: string,
  startDate: string,
  endDate: string,
  interval: 'day' | 'week' | 'month' = 'day',
): Promise<CacheHitRateResponse> {
  return apiCall<CacheHitRateResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_CACHE_HIT_RATE(siteId)}?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&interval=${interval}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}

/**
 * Fetch referenced topics for a site by attribution count.
 * @param order - 'highest' for most referenced, 'lowest' for least referenced (including no attribution)
 */
export async function fetchTopReferencedTopics(
  siteId: string,
  orgId: string,
  limit: number = 10,
  order: 'highest' | 'lowest' = 'highest',
): Promise<ReferencedTopicsResponse> {
  return apiCall<ReferencedTopicsResponse>(
    `${API_CONFIG.PLATFORM_BASE_URL}${PLATFORM_ENDPOINTS.USAGE_TOP_TOPICS(siteId)}?limit=${limit}&order=${order}`,
    {
      method: 'GET',
      headers: getHeadersWithOrg(),
    },
  );
}
